/**
 * Harness — what the interface harness found wrong with funder interfaces, and
 * how each gets corrected.
 *
 * Findings are conflicts, invalid values, rules without a source, sources that
 * changed or stopped answering, rules about to expire, and denial patterns that
 * point at a missing or wrong rule. Fix does one of four things, as the server
 * decides: queues the rules builder to re-draft the plan (redraft); opens the
 * plan's sources to add one (needs_source) or to change a failing URL
 * (edit_source); or opens the plan's rules with the ones involved marked
 * (edit_rules), where a person proposes the correction. The harness never
 * changes a rule itself; findings close on their own once the problem is gone.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldCheck, Wrench, X } from 'lucide-react';
import { funderAgentsApi, type FindingKind, type FunderFinding, type Severity } from '../../../shared/services/funderAgentsApi';
import { Btn, Empty, ErrorLine, NoteForm, SeverityBadge, errText, fmtDate, type Notify } from './shared';

const KIND_LABEL: Record<FindingKind, string> = {
  conflict: 'Conflicting rules',
  invalid_value: 'Invalid value',
  missing_source: 'No source',
  source_changed: 'Source changed',
  source_unreachable: 'Source unreachable',
  expiring: 'Expiring',
  denial_pattern: 'Denial pattern',
};

const ORDER: Record<Severity, number> = { high: 0, medium: 1, low: 2 };

/** Where a Fix sends the person when the server can't fix it by re-drafting. */
export type FixTarget =
  | { view: 'sources'; payerId: number; payerName: string; sourceId?: number | null; message: string }
  | { view: 'rules'; payerId: number; payerName: string; findingId: number; ruleIds: number[]; message: string };

type StatusFilter = 'open' | 'fixing' | 'all';

export default function HarnessView({
  notify,
  onRunStarted,
  onOpen,
}: {
  notify: Notify;
  onRunStarted: () => void;
  onOpen: (target: FixTarget) => void;
}) {
  const [items, setItems] = useState<FunderFinding[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<Severity | ''>('');
  const [dismissing, setDismissing] = useState<number | null>(null);
  const [status, setStatus] = useState<StatusFilter>('open');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await funderAgentsApi.findings({ status, severity: severity || undefined }));
    } catch (e) {
      setError(errText(e));
      setItems([]);
    }
  }, [severity, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () => [...(items ?? [])].sort((a, b) => (ORDER[a.severity] ?? 3) - (ORDER[b.severity] ?? 3) || (b.last_seen_at ?? '').localeCompare(a.last_seen_at ?? '')),
    [items],
  );

  const runAll = async () => {
    if (!window.confirm('Check every plan now?\n\nThe harness opens and closes findings; it never changes a rule.')) return;
    setBusy('run');
    try {
      const run = await funderAgentsApi.startRun({ agent: 'harness' });
      notify(true, `Harness run #${run?.id ?? ''} queued for all plans.`);
      onRunStarted();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const fix = async (f: FunderFinding) => {
    setBusy(`f${f.id}`);
    try {
      const res = await funderAgentsApi.fixFinding(f.id);
      const payerName = f.payer_name || `Plan ${f.payer_id}`;
      switch (res.action) {
        case 'redraft':
          notify(true, `Re-drafting this plan${res.run?.id ? ` (run #${res.run.id})` : ''}. New proposals will appear in Review.`);
          setItems((list) => (list ?? []).map((x) => (x.id === f.id ? { ...x, status: 'fixing' } : x)));
          onRunStarted();
          break;
        case 'needs_source':
          onOpen({ view: 'sources', payerId: f.payer_id, payerName, message: 'Add a source for this plan first, then fix the finding again.' });
          break;
        case 'edit_source':
          onOpen({
            view: 'sources',
            payerId: f.payer_id,
            payerName,
            sourceId: res.source?.id ?? f.source_id,
            message: 'This source keeps failing. Change its URL, or turn it off.',
          });
          break;
        case 'edit_rules':
        default: {
          const ruleIds = (res.rules ?? []).map((r) => r.id);
          onOpen({
            view: 'rules',
            payerId: f.payer_id,
            payerName,
            findingId: f.id,
            ruleIds: ruleIds.length ? ruleIds : f.rule_id ? [f.rule_id] : [],
            message: `${KIND_LABEL[f.kind] ?? f.kind}: ${f.message} Edit one of the marked rules; your change goes to Review.`,
          });
        }
      }
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (f: FunderFinding, note: string) => {
    try {
      await funderAgentsApi.dismissFinding(f.id, note);
      setDismissing(null);
      setItems((list) => (list ?? []).filter((x) => x.id !== f.id));
      notify(true, 'Finding dismissed.');
    } catch (e) {
      notify(false, errText(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold text-ink-700">
          Severity{' '}
          <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity | '')} className="ml-1 rounded-md border border-v2border bg-surface px-2 py-1 text-sm text-ink">
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-ink-700">
          Show{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} className="ml-1 rounded-md border border-v2border bg-surface px-2 py-1 text-sm text-ink">
            <option value="open">Open</option>
            <option value="fixing">Being re-drafted</option>
            <option value="all">All, including closed</option>
          </select>
        </label>
        <Btn tone="ghost" title="Refresh" onClick={() => void load()}>
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
        </Btn>
        <span className="ml-auto">
          <Btn tone="primary" busy={busy === 'run'} onClick={() => void runAll()}>
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
            Run harness now
          </Btn>
        </span>
      </div>
      <ErrorLine text={error} />
      {items === null ? (
        <p className="text-sm text-ink-500">Loading findings…</p>
      ) : sorted.length === 0 ? (
        <Empty>{status === 'all' ? 'No findings yet.' : 'No open findings.'} The harness checks every plan on its schedule; run it now to check after a change.</Empty>
      ) : (
        <ul className="space-y-3">
          {sorted.map((f) => {
            const closed = f.status === 'resolved' || f.status === 'dismissed';
            return (
              <li key={f.id} className="rounded-md border border-v2border bg-surface p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <SeverityBadge severity={f.severity} />
                  <span className="font-semibold text-ink">{KIND_LABEL[f.kind] ?? f.kind}</span>
                  <span className="text-ink-500">· {f.payer_name || `Plan ${f.payer_id}`}</span>
                  {f.program_type && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-paper-2 text-ink-700">{f.program_type}</span>}
                  {f.status === 'fixing' && <span className="text-xs text-v2accent-ink">Re-draft in progress</span>}
                  {closed && <span className="text-xs text-ink-500">{f.status === 'resolved' ? 'Resolved' : 'Dismissed'}</span>}
                  <span className="ml-auto text-xs text-ink-500">Seen {fmtDate(f.last_seen_at)}</span>
                </div>
                <p className="flex items-start gap-1.5 text-sm text-ink-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-attention" aria-hidden="true" />
                  {f.message}
                </p>
                {!closed && (
                <div className="flex flex-wrap gap-2">
                  <Btn tone="primary" busy={busy === `f${f.id}`} disabled={f.status === 'fixing'} onClick={() => void fix(f)}>
                    <Wrench className="w-3.5 h-3.5" aria-hidden="true" />
                    Fix
                  </Btn>
                  <Btn tone="danger" onClick={() => setDismissing(dismissing === f.id ? null : f.id)}>
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                    Dismiss
                  </Btn>
                </div>
                )}
                {dismissing === f.id && (
                  <NoteForm
                    label="Why dismiss it? (kept on record; the harness will not reopen the same finding)"
                    submitLabel="Dismiss finding"
                    onSubmit={(note) => dismiss(f, note)}
                    onCancel={() => setDismissing(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
